import { QuoteProductCommentSide } from '@codezo/graphql';
import { ThreadPanel, ThreadView, type ThreadMessage } from '@codezo/product-comments';
import { MoreHorizontal } from 'lucide-react';
import { useState } from 'react';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { FilePreview, type FilePreviewItem } from '../../components/ui/file-preview';
import { useProductThread } from '../../hooks/useProductThread';
import { timeAgo } from '../../lib/utils';

/**
 * The supplier's conversation with the studio about one product. Their thread
 * only — the backend fills in the supplier, and other suppliers' threads and
 * the studio's own notes never reach this app.
 */
export function JobThreadPanel({
	open,
	onClose,
	orderQuoteItemId,
	productName,
	onRead,
}: {
	open: boolean;
	onClose: () => void;
	orderQuoteItemId: string;
	productName: string;
	onRead: () => void;
}) {
	const thread = useProductThread({ orderQuoteItemId, skip: !open, onRead });
	const [viewer, setViewer] = useState<{ files: FilePreviewItem[]; index: number } | null>(null);
	const [deleteId, setDeleteId] = useState<string | null>(null);

	const openAttachment = (message: ThreadMessage, index: number) => {
		const files = message.attachments
			.map((a, i) => ({ a, i }))
			.filter(({ a }) => !!a.url)
			.map(({ a, i }) => ({
				id: `${message.id}-${i}`,
				url: a.url as string,
				name: a.name,
				mimeType: a.mimeType,
				size: a.size,
			}));
		const at = files.findIndex((f) => f.id === `${message.id}-${index}`);
		if (at >= 0) setViewer({ files, index: at });
	};

	return (
		<>
			<ThreadPanel
				open={open}
				onClose={onClose}
				title={productName}
				subtitle="Messages with 05 Creation"
			>
				<ThreadView
					className="flex-1"
					loading={thread.loading}
					onReachBottom={thread.reachedBottom}
					messages={{
						messages: thread.messages,
						labels: {
							formatTime: timeAgo,
							sideLabel: (side) =>
								side === QuoteProductCommentSide.Studio ? '05 Creation' : null,
						},
						emptyState:
							'Ask the studio about this product — artwork, specs, anything unclear.',
						onOpenAttachment: openAttachment,
						editingId: thread.editingId,
						candidates: thread.candidates,
						onEditSave: thread.saveEdit,
						onEditCancel: () => thread.setEditingId(null),
						editSaving: thread.editSaving,
						messageActions: (m) =>
							m.canEdit || m.canDelete ? (
								<DropdownMenu>
									<DropdownMenuTrigger
										aria-label="Message actions"
										className="flex size-6 shrink-0 cursor-pointer items-center justify-center rounded-md text-text-4 transition-colors hover:bg-surface-hover hover:text-text-1"
									>
										<MoreHorizontal size={14} />
									</DropdownMenuTrigger>
									<DropdownMenuContent align={m.isMine ? 'end' : 'start'}>
										{m.canEdit && (
											<DropdownMenuItem
												onClick={() => thread.setEditingId(m.id)}
											>
												Edit
											</DropdownMenuItem>
										)}
										{m.canDelete && (
											<DropdownMenuItem
												variant="destructive"
												onClick={() => setDeleteId(m.id)}
											>
												Delete
											</DropdownMenuItem>
										)}
									</DropdownMenuContent>
								</DropdownMenu>
							) : null,
					}}
					readOnlyNote="This conversation is no longer available."
					composer={
						thread.ended
							? undefined
							: {
									candidates: thread.candidates,
									pendingFiles: thread.pendingFiles,
									onAddFiles: thread.addFiles,
									onRemoveFile: thread.removeFile,
									onSend: thread.send,
									sending: thread.sending,
									preparing: thread.preparing,
									hint: 'The studio is notified',
								}
					}
				/>
			</ThreadPanel>

			{viewer && (
				<FilePreview
					files={viewer.files}
					initialIndex={viewer.index}
					onClose={() => setViewer(null)}
				/>
			)}
			<ConfirmDialog
				open={!!deleteId}
				onOpenChange={(o) => !o && setDeleteId(null)}
				title="Delete this message?"
				description="It's replaced by “Message deleted” for everyone in the thread."
				confirmLabel="Delete"
				onConfirm={async () => {
					if (deleteId) await thread.remove(deleteId);
					setDeleteId(null);
				}}
			/>
		</>
	);
}
